import { useMemo, useState } from 'react';

import { cx } from '../../lib/cx';
import { Spinner } from '../spinner';
import type { Column, TableProps } from './types';

type Sort = { key: string; direction: 'asc' | 'desc' } | null;

/**
 * A data table with sortable headers (announced via `aria-sort`), loading and empty states, and
 * an optional row action. Dense on purpose: the people reading it are looking things up.
 */
export const Table = <T,>({
  columns,
  rows,
  rowKey,
  caption,
  loading = false,
  empty,
  onRowClick,
}: TableProps<T>) => {
  const [sort, setSort] = useState<Sort>(null);

  const sorted = useMemo(() => {
    if (!sort) {
      return rows;
    }
    const column = columns.find((candidate) => candidate.key === sort.key);
    if (!column?.sortValue) {
      return rows;
    }
    const value = column.sortValue;
    return [...rows].sort((a, b) => {
      const left = value(a);
      const right = value(b);
      const order = left < right ? -1 : left > right ? 1 : 0;
      return sort.direction === 'asc' ? order : -order;
    });
  }, [rows, sort, columns]);

  const toggle = (column: Column<T>) => {
    if (!column.sortable) {
      return;
    }
    setSort((current) =>
      current?.key === column.key
        ? { key: column.key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key: column.key, direction: 'asc' },
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="border-b border-gray-100 bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
          <tr>
            {columns.map((column) => {
              const active = sort?.key === column.key;
              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={
                    active
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : column.sortable
                        ? 'none'
                        : undefined
                  }
                  className={cx(
                    'px-6 py-3',
                    column.align === 'right' && 'text-right',
                    column.className,
                  )}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => toggle(column)}
                      className="control-touch inline-flex items-center gap-1 uppercase hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    >
                      {column.header}
                      <span aria-hidden="true">
                        {active ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="px-6 py-8 text-center text-gray-400">
                <Spinner />
              </td>
            </tr>
          ) : sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-6 py-8 text-center text-sm text-gray-400">
                {empty ?? 'Sin registros'}
              </td>
            </tr>
          ) : (
            sorted.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cx(onRowClick && 'cursor-pointer hover:bg-gray-50')}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cx(
                      'px-6 py-3 text-gray-700',
                      column.align === 'right' && 'text-right',
                      column.className,
                    )}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
